import tailwindConfig from "tailwind.config";
import resolveConfig from "tailwindcss/resolveConfig";

import {
  PlayerDetailSeasonCategoricalScoresFragment,
  WatchlistPlayerSeasonCategoricalScoreFragment,
} from "@/generated/graphql";
import {
  cleanSeasonScores,
  createDataForCurrentPlayer,
  IScore,
} from "@/utils/handleDataCategoryMetrics";

/**
 * Cleans season categorical scores for the comparison charts.
 * @param data - Array of season categorical score fragments
 * @returns Array of cleaned score objects, with the season join key preserved
 * @remarks
 * Delegates to the shared {@link cleanSeasonScores}, which is the single
 * definition of what counts as transport noise on a season-scored row. SE-7494:
 * four surfaces each hand-rolled this filter and two of them dropped
 * `ggUnifiedSeasonKey` — the join key `createDataForCurrentPlayer` needs — so
 * their player series silently vanished. One definition, one contract.
 */
export const cleanedSeasonCategoricalScores = (
  data: WatchlistPlayerSeasonCategoricalScoreFragment[]
): IScore[] => cleanSeasonScores(data);

/**
 * Processes and merges categorical metrics for two players across seasons
 * Uses immutable reduce pattern to merge season data from both players into a unified format
 * Suitable for Victory chart rendering with proper color mapping and key ordering
 * @param currentPlayerScores - Categorical scores for the current player
 * @param currentPlayerId - ID of the current player
 * @param selectedPlayerScores - Categorical scores for the selected comparison player
 * @param selectedPlayerId - ID of the selected comparison player
 * @param key - The specific categorical score key to process (e.g., 'passing', 'defense')
 * @returns Object containing formatted data array, y-axis keys, and color mappings for chart rendering
 */
const getCategoryMetrics = (
  currentPlayerScores: IScore[],
  currentPlayerId: string,
  selectedPlayerScores: IScore[],
  selectedPlayerId: string,
  key: keyof PlayerDetailSeasonCategoricalScoresFragment
) => {
  const fullConfig = resolveConfig(tailwindConfig);
  const themeColors = fullConfig.theme.colors;

  const currentPlayerData = createDataForCurrentPlayer(
    currentPlayerScores,
    currentPlayerId,
    key
  );
  const selectedPlayerData = createDataForCurrentPlayer(
    selectedPlayerScores,
    selectedPlayerId,
    key
  );

  /**
   * Merged season data keyed by season number
   * Each season contains metric values for both players (if available)
   */
  interface SeasonMetricData {
    season: number;
    [metricKey: string]: number;
  }

  // Merge data by season for Victory chart compatibility using immutable reduce pattern
  const seasonData = [...currentPlayerData, ...selectedPlayerData].reduce(
    (seasons, item) => {
      const existing = seasons[item.season] || { season: item.season };
      return {
        ...seasons,
        [item.season]: { ...existing, ...item },
      };
    },
    {} as Record<number, SeasonMetricData>
  );

  const formatDatas = Object.values(seasonData).sort((a, b) =>
    a.season > b.season ? 1 : -1
  );

  const yKeys = Array.from(
    new Set(
      formatDatas.flatMap(d =>
        Object.keys(d).filter(k => k !== "season" && k.includes(key))
      )
    )
  );

  // Reorder yKeys to put the current player's key last
  const currentPlayerKey = `${key}_${currentPlayerId}`;
  const reorderedYKeys = [
    ...yKeys.filter(yKey => yKey !== currentPlayerKey),
    currentPlayerKey,
  ];

  const colors = reorderedYKeys.reduce(
    (acc, yKey) => ({
      ...acc,
      [yKey]:
        yKey === currentPlayerKey
          ? // @ts-expect-error TODO: figure out the proper way to extend the theme so we don't get typescript errors: https://gluestack.io/ui/docs/home/theme-configuration/customizing-theme
            themeColors.chart.player
          : // @ts-expect-error TODO: figure out the proper way to extend the theme so we don't get typescript errors: https://gluestack.io/ui/docs/home/theme-configuration/customizing-theme
            themeColors.chart.other,
    }),
    {} as Record<string, string>
  );

  return {
    data: formatDatas,
    yKeys: reorderedYKeys,
    colors,
  };
};

export { getCategoryMetrics };
